import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface DashboardMetrics {
  totalCompanies: number;
  activeCompanies: number;
  suspendedCompanies: number;
  totalUsers: number;
  activeUsers: number;
  suspendedUsers: number;
  deactivatedUsers: number;
  pendingRequests: number;
  noAdminCompanies: number;
}

const countOrThrow = (
  result: { count: number | null; error: { message: string } | null },
  label: string
): number => {
  if (result.error) throw new Error(`${label}: ${result.error.message}`);
  return result.count ?? 0;
};

const SYSTEM_COMPANY_ID = "2c24dd37-7434-4b8a-919e-2a54fb7aae20";

const fetchMetrics = async (): Promise<DashboardMetrics> => {
  // First batch: company counts + superuser ids + admin members + company ids
  const [
    totalCompaniesRes,
    activeCompaniesRes,
    suspendedCompaniesRes,
    pendingRequestsRes,
    allCompanyIdsRes,
    adminMembersRes,
    superuserIdsRes,
  ] = await Promise.all([
    supabase
      .from("companies")
      .select("*", { count: "exact", head: true })
      .neq("id", SYSTEM_COMPANY_ID),
    supabase
      .from("companies")
      .select("*", { count: "exact", head: true })
      .neq("id", SYSTEM_COMPANY_ID)
      .eq("status", "active"),
    supabase
      .from("companies")
      .select("*", { count: "exact", head: true })
      .neq("id", SYSTEM_COMPANY_ID)
      .eq("status", "suspended"),
    supabase
      .from("company_suspension_requests")
      .select("*", { count: "exact", head: true })
      .eq("status", "pending"),
    supabase
      .from("companies")
      .select("id")
      .neq("id", SYSTEM_COMPANY_ID),
    supabase
      .from("company_members")
      .select("company_id")
      .eq("role", "admin")
      .eq("status", "active"),
    supabase.from("admin_users").select("id"),
  ]);

  if (allCompanyIdsRes.error) {
    throw new Error(`companies list: ${allCompanyIdsRes.error.message}`);
  }
  if (adminMembersRes.error) {
    throw new Error(`active admins: ${adminMembersRes.error.message}`);
  }
  if (superuserIdsRes.error) {
    throw new Error(`superusers: ${superuserIdsRes.error.message}`);
  }

  const superuserIds = (superuserIdsRes.data ?? []).map(
    (r: { id: string }) => r.id
  );
  const superuserFilter =
    superuserIds.length > 0 ? `(${superuserIds.join(",")})` : null;

  const applySuperuserFilter = <T extends { not: (...args: any[]) => T }>(
    q: T
  ): T => (superuserFilter ? q.not("id", "in", superuserFilter) : q);

  // Second batch: profile counts excluding superusers
  const [totalUsersRes, activeUsersRes, suspendedUsersRes, deactivatedUsersRes] =
    await Promise.all([
      applySuperuserFilter(
        supabase.from("profiles").select("*", { count: "exact", head: true })
      ),
      applySuperuserFilter(
        supabase
          .from("profiles")
          .select("*", { count: "exact", head: true })
          .eq("status", "active")
      ),
      applySuperuserFilter(
        supabase
          .from("profiles")
          .select("*", { count: "exact", head: true })
          .eq("status", "suspended")
      ),
      applySuperuserFilter(
        supabase
          .from("profiles")
          .select("*", { count: "exact", head: true })
          .eq("status", "deactivated")
      ),
    ]);

  const allCompanyIds = new Set(
    (allCompanyIdsRes.data ?? []).map((c: { id: string }) => c.id)
  );
  const companiesWithAdmin = new Set(
    (adminMembersRes.data ?? []).map(
      (m: { company_id: string }) => m.company_id
    )
  );
  let noAdminCompanies = 0;
  allCompanyIds.forEach((id) => {
    if (!companiesWithAdmin.has(id)) noAdminCompanies += 1;
  });

  return {
    totalCompanies: countOrThrow(totalCompaniesRes, "total companies"),
    activeCompanies: countOrThrow(activeCompaniesRes, "active companies"),
    suspendedCompanies: countOrThrow(suspendedCompaniesRes, "suspended companies"),
    totalUsers: countOrThrow(totalUsersRes, "total users"),
    activeUsers: countOrThrow(activeUsersRes, "active users"),
    suspendedUsers: countOrThrow(suspendedUsersRes, "suspended users"),
    deactivatedUsers: countOrThrow(deactivatedUsersRes, "deactivated users"),
    pendingRequests: countOrThrow(pendingRequestsRes, "pending requests"),
    noAdminCompanies,
  };
};

export const useDashboardMetrics = () => {
  const query = useQuery({
    queryKey: ["dashboard-metrics"],
    queryFn: fetchMetrics,
    staleTime: 60_000,
  });

  return {
    metrics: query.data,
    isLoading: query.isLoading,
    error: query.isError ? "Failed to load metrics." : null,
    lastUpdated: query.dataUpdatedAt ? new Date(query.dataUpdatedAt) : null,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
};
